import { useEffect, useState } from 'react'
import {Landmark, Pencil, Plus, Search, Trash2} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import SecretariaModal from '@/components/secretarias/SecretariaModal'
import { formatDateBR } from '@/utils/masks'

function Secretarias() {
  const { user } = useAppAuth()
  const isAdmin = user?.role === 'ADMIN'

  const [secretarias, setSecretarias] = useState<any[]>([])
  const [cidades, setCidades] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  // Administrador enxerga todas as cidades (acesso total). Moderador fica preso à própria cidade.
  const [cidadeFiltro, setCidadeFiltro] = useState<string>(isAdmin ? 'TODAS' : user?.cidadeId || '')
  const [modalAberto, setModalAberto] = useState(false)
  const [secretariaEditando, setSecretariaEditando] = useState<any>(null)
  const [aExcluir, setAExcluir] = useState<any>(null)
  const [excluindo, setExcluindo] = useState(false)

  const fetchDados = async () => {
    setLoading(true)
    try {
      const [resSecretarias, resCidades] = await Promise.all([
        (lumi.entities as any).secretarias.list({ sort: { nome: 1 } }),
        (lumi.entities as any).cidades.list({ sort: { nome: 1 } }),
      ])
      setSecretarias(resSecretarias?.list || [])
      setCidades(resCidades?.list || [])
    }
    catch {
      toast.error('Erro ao carregar secretarias.')
    }
    finally {
      setLoading(false)
    }
  }

  const confirmarExclusao = async () => {
    if (!aExcluir?._id) return
    setExcluindo(true)
    try {
      await (lumi.entities as any).secretarias.delete(aExcluir._id)
      toast.success('Secretaria excluída com sucesso.')
      setAExcluir(null)
      await fetchDados()
    }
    catch {
      toast.error('Erro ao excluir secretaria. Tente novamente.')
    }
    finally {
      setExcluindo(false)
    }
  }

  useEffect(() => {
    fetchDados()
  }, [])

  // Moderador só pode gerir secretarias da própria cidade; Administrador vê e gere todas.
  const secretariasVisiveis = isAdmin ? secretarias : secretarias.filter(s => s.cidadeId === user?.cidadeId)
  const cidadesVisiveis = isAdmin ? cidades : cidades.filter(c => c._id === user?.cidadeId)

  const secretariasFiltradas = secretariasVisiveis.filter((s) => {
    const passaCidade = cidadeFiltro === 'TODAS' || s.cidadeId === cidadeFiltro
    const passaBusca = s.nome?.toLowerCase().includes(busca.toLowerCase())
    return passaCidade && passaBusca
  })

  const nomeCidade = (cidadeId: string) => cidades.find(c => c._id === cidadeId)?.nome || '-'

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[20px] font-semibold text-[#12141A]">Secretarias</h1>
          <p className="text-[13.5px] text-[#6B7280] mt-0.5">
            {isAdmin ? 'Gestão de todas as secretarias do sistema (acesso total).' : 'Secretarias da sua cidade.'}
          </p>
        </div>
        <button
          onClick={() => { setSecretariaEditando(null); setModalAberto(true) }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E4FBF] hover:bg-[#1A44A6] text-white text-[13.5px] font-medium"
        >
          <Plus className="w-4 h-4" strokeWidth={1.75} />
          Nova Secretaria
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-[320px]">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Pesquisar secretaria..."
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
          />
        </div>

        {isAdmin && (
          <select
            value={cidadeFiltro}
            onChange={e => setCidadeFiltro(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
          >
            <option value="TODAS">Todas as cidades</option>
            {cidades.map(c => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="border-b border-[#E7E9EC] bg-[#F9FAFB] text-left">
              <th className="px-5 py-3 font-medium text-[#6B7280]">Secretaria</th>
              {isAdmin && <th className="px-5 py-3 font-medium text-[#6B7280]">Cidade</th>}
              <th className="px-5 py-3 font-medium text-[#6B7280]">Criada em</th>
              <th className="px-5 py-3 font-medium text-[#6B7280] text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-[#9CA3AF]">A carregar...</td></tr>
            )}
            {!loading && secretariasFiltradas.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-[#9CA3AF]">Nenhuma secretaria encontrada.</td></tr>
            )}
            {!loading && secretariasFiltradas.map(s => (
              <tr key={s._id} className="border-b border-[#F1F2F4] last:border-0 hover:bg-[#F9FAFB]">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-[#FFF1E7] flex items-center justify-center shrink-0">
                      <Landmark className="w-4 h-4 text-[#C2560B]" strokeWidth={1.75} />
                    </div>
                    <span className="font-medium text-[#12141A]">{s.nome}</span>
                  </div>
                </td>
                {isAdmin && <td className="px-5 py-3.5 text-[#4B5563]">{nomeCidade(s.cidadeId)}</td>}
                <td className="px-5 py-3.5 text-[#4B5563]">{formatDateBR(s.createdAt)}</td>
                <td className="px-5 py-3.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      onClick={() => { setSecretariaEditando(s); setModalAberto(true) }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-[#374151] border border-[#D1D5DB] hover:bg-[#F5F6F8]"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                      Editar
                    </button>
                    <button
                      onClick={() => setAExcluir(s)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-[#B91C1C] border border-[#FCA5A5] hover:bg-[#FEF2F2]"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {aExcluir && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-[420px] p-6">
            <div className="w-10 h-10 rounded-xl bg-[#FEF2F2] flex items-center justify-center mb-4">
              <Trash2 className="w-5 h-5 text-[#B91C1C]" strokeWidth={1.75} />
            </div>
            <h2 className="text-[16px] font-semibold text-[#12141A]">Excluir secretaria</h2>
            <p className="text-[13.5px] text-[#6B7280] mt-1.5">
              Tem a certeza de que deseja excluir <strong className="text-[#12141A]">{aExcluir.nome}</strong>? Esta ação não pode ser anulada.
            </p>
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setAExcluir(null)}
                disabled={excluindo}
                className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarExclusao}
                disabled={excluindo}
                className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-60"
              >
                {excluindo ? 'A excluir...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalAberto && (
        <SecretariaModal
          secretaria={secretariaEditando}
          cidades={cidadesVisiveis}
          cidadeIdFixa={isAdmin ? undefined : user?.cidadeId}
          onClose={() => setModalAberto(false)}
          onSaved={fetchDados}
        />
      )}
    </div>
  )
}

export default Secretarias
